import React from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import { InfoOutlined, WarningAmber } from '@mui/icons-material';
import PrimaryButton from '../buttons/PrimaryButton';
import SecondaryButton from '../buttons/SecondaryButton';
import ScreenReaderTitle from './ScreenReaderTitle';

interface ConfirmationProps {
  open: boolean;
  title?: string;
  accessibleTitle: string; // not all dialogs will have a visual title, but all must have a title for screen readers.
  message: string;
  onConfirm: (...args: any[]) => void;
  onCancel: () => void;
  confirmText?: string;
  cancelText?: string;
  icon?: 'info' | 'error';
  confirmButtonSx?: object;
}

const icons = {
  info: InfoOutlined,
  error: WarningAmber,
};

export const Confirmation: React.FC<ConfirmationProps> = ({
  open,
  title,
  accessibleTitle,
  message,
  onConfirm,
  onCancel,
  confirmText = 'Delete',
  cancelText = 'Cancel',
  icon = 'error',
  confirmButtonSx,
}) => {
  // sanitize for the id/aria connection but leave it for the reading
  const sanitizedTitle = accessibleTitle.replace(/\s+/g, '-');
  const Icon = icons[icon] ?? WarningAmber;
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="xs"
      fullWidth
      aria-labelledby={`${sanitizedTitle}-dialog-title`}
      slotProps={{ paper: { style: { borderRadius: 8 } } }}
    >
      <DialogTitle>
        {title && (
          <>
            <span>{title}</span>
          </>
        )}
        <ScreenReaderTitle accessibleTitle={accessibleTitle} sanitizedTitle={sanitizedTitle} />
      </DialogTitle>
      <DialogContent sx={{ padding: 3, paddingBottom: 0, textAlign: 'center' }}>
        <Icon sx={{ fontSize: 40, margin: 1, color: icon === 'error' ? 'error.main' : 'primary.dark' }} />
        <DialogContentText>{message}</DialogContentText>
      </DialogContent>
      <DialogActions sx={{ padding: 3 }}>
        <SecondaryButton action={onCancel}>{cancelText}</SecondaryButton>
        <PrimaryButton action={onConfirm} loading={false} disabled={false} sx={confirmButtonSx}>
          {confirmText}
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
};

export default Confirmation;
